import type { ServerErrorCode } from './protocol-types';

/** Inputs that determine the full-page connection failure presentation. */
export interface ConnectionErrorPresentationInput {
  code: ServerErrorCode | null;
  fallbackMessage: string;
  retryable: boolean;
  authorizedConversationId: string | null;
  authorizedUnavailableReason: 'insufficient_credits' | null;
}

/** A verified navigation option for a connection failure. */
export interface ConnectionErrorLink {
  /** Visible label that names the destination. */
  label: string;
  /** Internal route reached by the link. */
  to: string;
  /** Visual priority within the blocking state. */
  emphasis: 'primary' | 'secondary';
}

/** User-facing content and recovery controls for a connection failure. */
export interface ConnectionErrorPresentation {
  title: string;
  message?: string;
  retryable: boolean;
  links: ConnectionErrorLink[];
}

/** Resolve a safe connection failure presentation for the verified audience. */
export function resolveConnectionErrorPresentation(
  input: ConnectionErrorPresentationInput,
): ConnectionErrorPresentation {
  if (input.code === 'conversation_unavailable') {
    if (
      input.authorizedConversationId
      && input.authorizedUnavailableReason === 'insufficient_credits'
    ) {
      return {
        title: 'No credits left',
        message: "This conversation cannot start. People using your link won't see this message.",
        retryable: false,
        links: [
          {
            label: 'View billing',
            to: '/admin/billing',
            emphasis: 'primary',
          },
          {
            label: 'Back to conversation',
            to: `/admin/interviews/${encodeURIComponent(input.authorizedConversationId)}`,
            emphasis: 'secondary',
          },
        ],
      };
    }

    if (input.authorizedConversationId) {
      return {
        title: 'Conversation unavailable',
        message: "This conversation can't start right now.",
        retryable: false,
        links: [{
          label: 'Back to conversation',
          to: `/admin/interviews/${encodeURIComponent(input.authorizedConversationId)}`,
          emphasis: 'secondary',
        }],
      };
    }

    return {
      title: 'Conversation unavailable',
      message: "This conversation can't start right now. Contact the person who shared this link.",
      retryable: false,
      links: [],
    };
  }

  if (input.code === 'service_unavailable') {
    return {
      title: 'Conversation unavailable',
      message: 'Please try again in a moment.',
      retryable: input.retryable,
      links: [{
        label: 'Return to join page',
        to: '/',
        emphasis: 'secondary',
      }],
    };
  }

  if (input.code === 'realtime_unavailable') {
    return {
      title: 'Connection error',
      message: 'We could not start the conversation. Please try again.',
      retryable: input.retryable,
      links: [{
        label: 'Return to join page',
        to: '/',
        emphasis: 'secondary',
      }],
    };
  }

  return {
    title: 'Connection error',
    message: input.fallbackMessage,
    retryable: input.retryable,
    links: [{
      label: 'Return to join page',
      to: '/',
      emphasis: 'secondary',
    }],
  };
}
